import { ApiError } from '../../api';
import { t } from '../../i18n';

/**
 * What a screen shows when the load failed.
 *
 * Twenty files carry their own copy of `e instanceof ApiError ? e.message :
 * t('Loading failed')`, most of them wrapped in the same red card with the
 * same "Try again" underneath — and the ones that forgot the retry left the
 * person with nothing to do but change screens and come back. The kit already
 * had EmptyState and Skeleton for the other two outcomes of a fetch; this is
 * the third.
 *
 * `error` is whatever the query threw, unwrapped here rather than at the call
 * site: the server has usually written a better sentence than we would, and
 * the fallback is only for the failures that are not the server's.
 */
export function ErrorState({
  error,
  onRetry,
  className = '',
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={`rounded-card border border-urgent/25 bg-urgent/10 p-5 text-sm font-semibold text-urgent ${className}`}
    >
      <p dir="auto">{errorMessage(error)}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-2xl bg-urgent/10 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors active:bg-urgent/20"
        >
          {t('Try again')}
        </button>
      )}
    </div>
  );
}

/** The server's sentence when there is one, ours when there is not. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return t('Loading failed');
}
